// src/components/landing/LandingPage.jsx
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpen,
  BarChart3,
  Users,
  FileText,
  TrendingUp,
  Target,
  Share2,
  CheckCircle2,
  ArrowRight,
  Play,
  Star,
  Award,
  Clock,
  Shield,
  Zap,
  Calendar,
  PieChart,
  Calculator,
  Eye,
  Download,
  ChevronRight,
  Brain,
  Sparkles,
  MessageSquare,
  Lightbulb,
  TrendingDown,
  AlertTriangle,
  Crown,
  DollarSign,
  Timer,
  Gift
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import Navbar from '../common/Navbar'; 
import ScheduleDemoForm from './ScheduleDemoForm';
import logo from '../../assets/images/logo.png';

const LandingPage = () => {
  const { currentUser } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [demoModalOpen, setDemoModalOpen] = useState(false);

  const getEarlyBirdCountdown = () => {
    const earlyBirdEnd = new Date('2025-06-30');
    const now = new Date();
    const timeDiff = earlyBirdEnd - now;
    
    if (timeDiff <= 0) return null;
    
    const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    return { days, hours };
  };

  const countdown = getEarlyBirdCountdown();

  // Updated pricing plans with actual subscription data
  const pricingPlans = [
    {
      id: 'individual-term',
      name: 'Individual - Term',
      description: 'Perfect for individual educators',
      price: 180,
      period: 'per term',
      duration: '4 months',
      popular: false,
      features: [
        'Unlimited assessment tasks',
        'Performance analytics',
        'Submission tracking',
        'Basic reports',
        'Cloud storage (2GB)',
        '14-day free trial'
      ]
    },
    {
      id: 'individual-annual',
      name: 'Individual - Annual',
      description: 'Best value for individual educators',
      price: 300,
      originalPrice: 600,
      period: 'per year',
      duration: '12 months',
      popular: true,
      isEarlyBird: !!countdown,
      savings: 300,
      features: [
        'Everything in Term plan',
        'AI-powered reports (20/year)',
        'Priority support',
        'Advanced analytics',
        'Collaboration tools',
        'Cloud storage (10GB)',
        'Performance predictions',
        '50% Early Bird Discount!'
      ]
    },
    {
      id: 'school-annual',
      name: 'School - Annual',
      description: 'For schools and institutions',
      price: 120,
      originalPrice: 240,
      period: 'per educator/year',
      duration: '12 months',
      minUsers: 5,
      isEarlyBird: !!countdown,
      savings: 120,
      features: [
        'All Individual features',
        'Multi-educator management',
        'School-wide analytics',
        'Admin dashboard',
        'Bulk user management',
        'Custom branding',
        'API access',
        '50% Early Bird Discount!'
      ]
    },
    {
      id: 'ai-reports',
      name: 'AI Reports Add-on',
      description: 'Intelligent performance insights',
      price: 60,
      period: 'per learner/year',
      duration: '12 months',
      isAddon: true,
      features: [
        'AI-powered analysis',
        'Personalized recommendations',
        'Learning gap identification',
        'Up to 40 reports per subject',
        'Predictive insights',
        'Parent-friendly reports'
      ]
    }
  ];

  const features = [
    {
      icon: FileText,
      title: "Smart Task Creation",
      description: "Create comprehensive assessment tasks with itemized marking, multiple question types, and integrated file management.",
      color: "purple"
    },
    {
      icon: Users,
      title: "Submission Tracking", 
      description: "Monitor learner submissions in real-time with attendance tracking and automated submission rate calculations.",
      color: "green"
    },
    {
      icon: Brain,
      title: "AI-Powered Reports",
      description: "Generate intelligent performance reports with actionable insights, learning gap analysis, and personalized recommendations using advanced AI.",
      color: "primary",
      featured: true
    },
    {
      icon: BarChart3,
      title: "Performance Analytics",
      description: "Detailed performance reports with statistical analysis, quartile distributions, and topic-based insights.",
      color: "blue"
    },
    {
      icon: Share2,
      title: "Collaborative Sharing",
      description: "Share assessment tasks with colleagues and collaborate across schools and districts seamlessly.",
      color: "orange"
    },
    {
      icon: Target,
      title: "Learning Gap Analysis",
      description: "Identify learning gaps through itemized marking and provide targeted feedback for improved outcomes.",
      color: "red"
    }
  ];

  const benefits = [
    {
      icon: Brain,
      title: "AI-Driven Insights",
      description: "Get intelligent recommendations and actionable insights powered by advanced AI analysis.",
      stat: "94% accuracy rate"
    },
    {
      icon: Clock,
      title: "Save Time",
      description: "Reduce administrative overhead with automated calculations and streamlined workflows.",
      stat: "15 hours saved/week"
    },
    {
      icon: Award,
      title: "Improve Outcomes",
      description: "Data-driven insights help identify learning gaps and inform teaching strategies.",
      stat: "23% improvement"
    },
    {
      icon: Shield,
      title: "Secure & Compliant",
      description: "Enterprise-grade security ensures learner data protection and privacy compliance.",
      stat: "POPIA compliant"
    }
  ];

  const stats = [
    { number: "50,000+", label: "Assessments Created", icon: FileText },
    { number: "500,000+", label: "Learner Records", icon: Users },
    { number: "2,000+", label: "Educators", icon: BookOpen },
    { number: "10,000+", label: "AI Reports Generated", icon: Brain }
  ];

  const testimonials = [
    {
      name: "Sarah Johnson",
      role: "Grade 9 Mathematics Teacher",
      school: " ",
      content: "The AI reports have revolutionized how I understand my learners' progress. The early bird pricing made it affordable for our school.",
      rating: 5
    },
    {
      name: "Michael Nkomo", 
      role: "Head of Department",
      school: " ",
      content: "SmartRegister's AI analysis identified learning gaps we hadn't noticed. The school subscription saved us thousands compared to individual licenses.",
      rating: 5
    },
    {
      name: "Dr Priya Patel",
      role: "District Official",
      school: " ",
      content: "The district-wide analytics provide invaluable insights. The subscription model works perfectly for our budget planning.",
      rating: 5
    }
  ];

  const faqs = [
    {
      question: "What's included in the early bird special?",
      answer: "50% off all annual subscriptions until June 30, 2025. This includes individual and school plans, with full access to AI reports."
    },
    {
      question: "Can I switch between plans?",
      answer: "Yes, you can upgrade or change plans anytime. We'll prorate the pricing and apply credits to your account."
    },
    {
      question: "How does the school pricing work?",
      answer: "School plans are billed per educator with a minimum of 5 users. Each educator gets full access to all features."
    },
    {
      question: "What happens after the early bird period?",
      answer: "Annual plans return to regular pricing (R600 individual, R240 per educator for schools). Current subscribers keep their discounted rate."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Use the Navbar component */}
      <Navbar variant="landing" />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#a9a6c4]/20 via-white to-[#887fac]/20 py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Hero Content */}
            <div>
              {/* Early Bird Badge */}
              {countdown && (
                <div className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-100 to-red-100 text-orange-800 px-4 py-2 rounded-full text-sm font-medium mb-6 border border-orange-300">
                  <Timer className="w-4 h-4" />
                  <span className="font-bold">50% OFF</span>
                  <span>•</span>
                  <span>{countdown.days} days {countdown.hours} hours left!</span>
                </div>
              )}
              
              <h1 className="text-3xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
                Master Assessment
                <span className="block text-[#6e55a3]">Management</span>
                <span className="block">with AI Intelligence</span>
              </h1>
              
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                From task creation to AI-powered performance analysis, SmartRegister streamlines your assessment workflow 
                and provides intelligent insights to improve learner outcomes.
              </p>

              {/* Pricing Highlight */}
              {countdown && (
                <div className="bg-white rounded-lg border-2 border-[#6e55a3] p-4 mb-8">
                  <div className="flex items-center gap-3 mb-2">
                    <Gift className="w-5 h-5 text-[#6e55a3]" />
                    <span className="font-semibold text-gray-900">Early Bird Special</span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Individual Annual:</span>
                      <div className="font-bold text-[#6e55a3]">
                        <span className="line-through text-gray-400 text-xs">R600</span> R300/year
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-600">School Annual:</span>
                      <div className="font-bold text-[#6e55a3]">
                        <span className="line-through text-gray-400 text-xs">R240</span> R120/educator/year
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Link 
                  to={currentUser ? "/pricing" : "/register"}
                  className="bg-[#6e55a3] text-white px-8 py-4 rounded-lg font-semibold hover:bg-[#6156a6] transition-colors flex items-center justify-center gap-2"
                >
                  {countdown ? (
                    <>
                      <Gift className="w-5 h-5" />
                      Claim 50% Discount
                    </>
                  ) : (
                    <>
                      Start Free Trial
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </Link>
                <button className="border border-gray-300 text-gray-700 px-8 py-4 rounded-lg font-semibold hover:bg-gray-50 transition-colors flex items-center justify-center gap-2">
                  <Play className="w-5 h-5" />
                  Watch Demo
                </button>
              </div>
              
              <div className="flex items-center gap-6 text-sm text-gray-500">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                  14-day free trial
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                  No credit card required
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                  {countdown ? "50% off annual plans" : "AI reports included"}
                </div>
              </div>
            </div>

            {/* Hero Visual */}
            <div className="relative">
              <div className="bg-white rounded-2xl shadow-2xl p-6 transform rotate-3 hover:rotate-0 transition-transform duration-300">
                <div className="bg-gradient-to-r from-[#6e55a3] to-[#887fac] rounded-lg p-4 mb-4">
                  <div className="flex items-center gap-3 text-white">
                    <Brain className="w-6 h-6" />
                    <h3 className="font-semibold">AI Performance Analysis</h3>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Class Average</span>
                    <span className="font-semibold text-green-600">78.5%</span>
                  </div>
                  
                  <div className="bg-[#a9a6c4]/10 p-3 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles className="w-4 h-4 text-[#6e55a3]" />
                      <span className="text-xs font-medium text-[#6156a6]">AI Recommendation</span>
                    </div>
                    <p className="text-xs text-gray-700">Focus on algebraic thinking concepts. 23% of learners show gaps in this area.</p>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Outstanding (80-100%)</span>
                      <span>35%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-green-500 h-2 rounded-full" style={{ width: '35%' }}></div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Floating pricing element */}
              {countdown && (
                <div className="absolute -top-4 -right-4 bg-gradient-to-r from-orange-500 to-red-500 text-white p-3 rounded-xl shadow-lg">
                  <div className="text-center">
                    <div className="text-xs font-bold">SAVE</div>
                    <div className="text-lg font-bold">50%</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="flex justify-center mb-4">
                  <div className="bg-[#a9a6c4]/20 p-3 rounded-full">
                    <stat.icon className="w-6 h-6 text-[#6e55a3]" />
                  </div>
                </div>
                <div className="text-3xl lg:text-4xl font-bold text-gray-900 mb-2">{stat.number}</div>
                <div className="text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Choose Your Perfect
              <span className="block text-[#6e55a3]">Task Management Plan</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-6">
              Streamline your assessment workflow with intelligent analytics. 
              {countdown && " Take advantage of our limited-time early bird pricing!"}
            </p>

            {/* Early Bird Alert */}
            {countdown && (
              <div className="bg-gradient-to-r from-orange-100 to-red-100 border border-orange-300 rounded-lg p-4 max-w-md mx-auto">
                <div className="flex items-center justify-center gap-2 text-orange-800 mb-2">
                  <Timer className="w-5 h-5" />
                  <span className="font-semibold">Early Bird Special!</span>
                </div>
                <p className="text-sm text-orange-700 mb-2">
                  50% off annual plans ends in:
                </p>
                <div className="text-lg font-bold text-orange-900">
                  {countdown.days} days, {countdown.hours} hours
                </div>
              </div>
            )}
          </div>

          {/* Pricing Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {pricingPlans.map((plan) => {
              const isPopular = plan.popular;
              const isAddon = plan.isAddon;
              
              return (
                <div 
                  key={plan.id}
                  className={`relative bg-white rounded-2xl border-2 transition-all duration-300 hover:shadow-xl ${
                    isPopular 
                      ? 'border-[#6e55a3] shadow-lg scale-105' 
                      : isAddon 
                      ? 'border-purple-300'
                      : 'border-gray-200 hover:border-[#6e55a3]'
                  }`}
                >
                  {/* Popular Badge */}
                  {isPopular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <div className="bg-[#6e55a3] text-white px-4 py-1 rounded-full text-sm font-medium flex items-center gap-1">
                        <Crown className="w-4 h-4" />
                        Most Popular
                      </div>
                    </div>
                  )}

                  {/* Early Bird Badge */}
                  {plan.isEarlyBird && (
                    <div className="absolute -top-2 -right-2">
                      <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white p-2 rounded-full">
                        <Gift className="w-4 h-4" />
                      </div>
                    </div>
                  )}

                  <div className="p-8">
                    {/* Plan Header */}
                    <div className="text-center mb-6">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                      <p className="text-gray-600 text-sm">{plan.description}</p>
                    </div>

                    {/* Pricing */}
                    <div className="text-center mb-6">
                      {plan.originalPrice && plan.isEarlyBird ? (
                        <div>
                          <div className="text-sm text-gray-500 line-through">R{plan.originalPrice}</div>
                          <div className="text-3xl font-bold text-[#6e55a3]">
                            R{plan.price}
                          </div>
                          <div className="text-sm text-green-600 font-medium">
                            Save R{plan.savings}!
                          </div>
                        </div>
                      ) : (
                        <div className="text-3xl font-bold text-gray-900">
                          R{plan.price}
                        </div>
                      )}
                      
                      <div className="text-sm text-gray-600 mt-1">
                        {plan.period}
                      </div>

                      {plan.minUsers && (
                        <div className="text-xs text-gray-500 mt-1">
                          Minimum {plan.minUsers} educators
                        </div>
                      )}
                    </div>

                    {/* Features */}
                    <div className="space-y-3 mb-8">
                      {plan.features.map((feature, index) => (
                        <div key={index} className="flex items-start gap-3">
                          <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-gray-700">{feature}</span>
                        </div>
                      ))}
                    </div>

                    {/* CTA Button */}
                    <Link
                      to={currentUser ? "/pricing" : "/register"}
                      className={`w-full py-3 px-4 rounded-lg font-semibold transition-all duration-300 flex items-center justify-center gap-2 ${
                        isPopular
                          ? 'bg-[#6e55a3] text-white hover:bg-[#6156a6] shadow-lg'
                          : isAddon
                          ? 'bg-purple-600 text-white hover:bg-purple-700'
                          : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                      }`}
                    >
                      {plan.isEarlyBird ? (
                        <>
                          <Gift className="w-4 h-4" />
                          Get 50% Off
                        </>
                      ) : (
                        <>
                          {isPopular ? 'Start Free Trial' : 'Choose Plan'}
                          <ArrowRight className="w-4 h-4" />
                        </>
                      )}
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pricing FAQ */}
          <div className="mt-16 bg-gray-50 rounded-2xl p-8">
            <h3 className="text-xl font-bold text-center mb-8">Frequently Asked Questions</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {faqs.map((faq, index) => (
                <div key={index}>
                  <h4 className="font-semibold text-gray-900 mb-2">{faq.question}</h4>
                  <p className="text-gray-600 text-sm">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Everything You Need for
              <span className="block text-[#6e55a3]">Assessment Excellence</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive tools designed for South African educators to create, 
              manage, and analyze assessments with AI-powered intelligence.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const colorClasses = {
                primary: "bg-[#a9a6c4]/20 text-[#6e55a3]",
                blue: "bg-blue-100 text-blue-600",
                green: "bg-green-100 text-green-600", 
                purple: "bg-purple-100 text-purple-600",
                orange: "bg-orange-100 text-orange-600",
                red: "bg-red-100 text-red-600"
              };

              return (
                <div key={index} className={`bg-white p-8 rounded-xl border border-gray-200 hover:shadow-lg transition-shadow group ${feature.featured ? 'ring-2 ring-[#6e55a3] relative' : ''}`}>
                  {feature.featured && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                      <span className="bg-[#6e55a3] text-white px-3 py-1 rounded-full text-xs font-medium">
                        AI-Powered
                      </span>
                    </div>
                  )}
                  <div className={`inline-flex p-3 rounded-lg ${colorClasses[feature.color]} mb-4`}>
                    <feature.icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Why Educators Choose
              <span className="block text-[#6e55a3]">SmartRegister</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Join thousands of South African educators who have transformed their assessment 
              practices and improved learner outcomes with our AI-powered platform.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center">
                <div className="bg-[#a9a6c4]/20 p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <benefit.icon className="w-8 h-8 text-[#6e55a3]" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                <p className="text-gray-600 mb-3">{benefit.description}</p>
                <div className="text-[#6e55a3] font-bold text-lg">{benefit.stat}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Trusted by Leading
              <span className="block text-[#6e55a3]">Educators</span>
            </h2>
            <p className="text-xl text-gray-600">
              See how SmartRegister's subscription plans are transforming education across South Africa
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white p-8 rounded-xl shadow-lg">
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                
                <p className="text-gray-700 mb-6 leading-relaxed">
                  "{testimonial.content}"
                </p>
                
                <div>
                  <div className="font-semibold text-gray-900">{testimonial.name}</div>
                  <div className="text-[#6e55a3] text-sm">{testimonial.role}</div>
                  <div className="text-gray-500 text-sm">{testimonial.school}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#6e55a3] to-[#6156a6]">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
            Ready to Transform Your
            <span className="block">Assessment Process?</span>
          </h2>
          
          <p className="text-xl text-white/90 mb-8">
            Join thousands of South African educators who have already revolutionized their 
            assessment workflows with intelligent analytics and AI-powered insights.
          </p>

          {/* Early Bird CTA */}
          {countdown && (
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 mb-8">
              <div className="flex items-center justify-center gap-2 text-white mb-2">
                <Timer className="w-5 h-5" />
                <span className="font-bold text-lg">Limited Time: 50% OFF Annual Plans!</span>
              </div>
              <p className="text-white/90 text-sm">
                Early bird pricing ends in {countdown.days} days, {countdown.hours} hours
              </p>
            </div>
          )}
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to={currentUser ? "/pricing" : "/register"}
              className="bg-white text-[#6e55a3] px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors flex items-center justify-center gap-2"
            >
              {countdown ? (
                <>
                  <Gift className="w-5 h-5" />
                  Claim 50% Discount
                </>
              ) : (
                <>
                  Start Your Free Trial
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </Link>
            <button 
              onClick={() => setDemoModalOpen(true)}
              className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-[#6e55a3] transition-colors"
            >
              Schedule Demo
            </button>
          </div>
          
          <p className="text-white/80 text-sm mt-6">
            {countdown ? "50% discount • " : ""}14-day free trial • No credit card required • Cancel anytime
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Company Info */}
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <img src={logo} alt="SmartRegister" className="h-8 w-auto brightness-0 invert" />
                
              </div>
              <p className="text-gray-300 mb-6 max-w-md">
                Empowering South African educators with AI-powered class management 
                tools and flexible subscription plans to improve teaching effectiveness.
              </p>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 text-gray-300">
                  <Shield className="w-4 h-4" />
                  <span className="text-sm">POPIA Compliant</span>
                </div>
                <div className="flex items-center gap-2 text-gray-300">
                  <Brain className="w-4 h-4" />
                  <span className="text-sm">AI-Powered</span>
                </div>
              </div>
            </div>

            {/* Product Links */}
            <div>
              <h3 className="font-semibold mb-4">Product</h3>
              <ul className="space-y-2 text-gray-300">
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">Pricing</a></li>
                <li><a href="#ai-reports" className="hover:text-white transition-colors">AI Reports</a></li>
                <li><Link to="/subscription" className="hover:text-white transition-colors">Subscriptions</Link></li>
              </ul>
            </div>

            {/* Support Links */}
            <div>
              <h3 className="font-semibold mb-4">Support</h3>
              <ul className="space-y-2 text-gray-300">
                <li><a href="#help" className="hover:text-white transition-colors">Help Center</a></li>
                <li><a href="mailto:support@SmartRegister.co.za" className="hover:text-white transition-colors">Contact Us</a></li>
                <li><a href="#billing" className="hover:text-white transition-colors">Billing Support</a></li>
                <li><a href="#status" className="hover:text-white transition-colors">System Status</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm">
              © 2025 SmartRegister. All rights reserved.
            </p>
            <div className="flex items-center gap-6 mt-4 md:mt-0">
              <a href="#privacy" className="text-gray-400 hover:text-white text-sm transition-colors">
                Privacy Policy
              </a>
              <a href="#terms" className="text-gray-400 hover:text-white text-sm transition-colors">
                Terms of Service
              </a>
              <a href="#billing" className="text-gray-400 hover:text-white text-sm transition-colors">
                Billing Terms
              </a>
            </div>
          </div>
        </div>
      </footer>
      <ScheduleDemoForm isOpen={demoModalOpen} onClose={() => setDemoModalOpen(false)} />
    </div>
  );
};

export default LandingPage;